import { useState, useCallback, useEffect } from "react";
import { Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { compressImageFile } from "@/lib/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Galería de evidencias del vehículo (fotos/daños/objetos). Autocontenido: gestiona su propio estado y API.
export function PhotoDialog({ valet, onClose, onChanged, defaultTipo = "Frente" }) {
  const [tipo, setTipo] = useState(defaultTipo);
  const [fotos, setFotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadFotos = useCallback(async () => {
    try { const { data } = await api.get(`/operativa/evidencias?id_ticket=${valet.id_ticket}`); setFotos(data); } catch {}
  }, [valet.id_ticket]);
  useEffect(() => { loadFotos(); }, [loadFotos]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImageFile(file, { maxSize: 900, quality: 0.65 });
      await api.post("/operativa/evidencias", { id_ticket: valet.id_ticket, tipo_evidencia: tipo, url_imagen: dataUrl });
      toast.success(`Foto (${tipo}) comprimida y guardada`);
      loadFotos();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error al procesar imagen"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-[#0A0A0A] bg-[#0A0A0A] text-white max-h-[90dvh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-white">Evidencias · {valet.folio_impreso}</DialogTitle>
          <DialogDescription className="text-white/50">Captura múltiples fotos. La imagen se comprime en el dispositivo (máx 900px, calidad 65%) antes de subirse.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 pt-2">
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="foto-tipo" className="h-11 rounded-none bg-white/5 border-white/20 text-white w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Frente">Frente</SelectItem>
              <SelectItem value="Placa">Placa</SelectItem>
              <SelectItem value="Daño">Daño</SelectItem>
              <SelectItem value="Objetos de Valor">Objetos de Valor</SelectItem>
              <SelectItem value="Daños Preexistentes">Daños Preexistentes</SelectItem>
            </SelectContent>
          </Select>
          <label data-testid="foto-capturar" className="btn-hard flex-1 h-11 bg-[#FFCC00] text-[#0A0A0A] border border-[#FFCC00] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 cursor-pointer">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Camera className="h-4 w-4" /> Tomar foto</>}
            <input type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4 max-h-64 overflow-auto" data-testid="foto-galeria">
          {fotos.length === 0 ? <p className="col-span-3 text-white/40 text-sm text-center py-6">Sin evidencias aún.</p>
          : fotos.map((f) => (
            <div key={f.id_foto} className="relative border border-white/15">
              <img src={f.url_imagen} alt={f.tipo_evidencia} className="w-full h-24 object-cover" />
              <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[10px] uppercase tracking-wider font-bold px-1 py-0.5 text-[#FFCC00]">{f.tipo_evidencia}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoDialog;
